// src/hooks/useLowPowerMode.js
// Emergency Low Power Mode.
//
// Activates automatically when:
//   - Battery level falls below 20% (Battery Status API)
//   - User manually enables it
//   - Stored in localStorage so it survives page reloads
//
// What changes in low power mode:
//   - Background refresh intervals multiplied by LOW_POWER_MULTIPLIER (4×)
//   - GPS polling paused — position only read on explicit user action
//   - Animations disabled (CSS class on root element)
//   - Non-critical images skipped
//   - Cached data served first, network requests deferred
//   - Dark mode forced (OLED screens save power with dark pixels)
//
// What never changes:
//   - SOS functionality — always full power
//   - Emergency contacts — always accessible
//   - Offline map reads — no network anyway

import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY        = 'eos-low-power-mode';
const LOW_BATTERY_THRESH = 0.20;   // 20%
const LOW_POWER_MULTIPLIER = 4;    // refresh intervals 4× longer

// Multiplied refresh intervals (milliseconds)
export const LOW_POWER_REFRESH = {
  alerts:       12 * 60 * 1000,   // 3min → 12min
  traffic:      20 * 60 * 1000,   // 5min → 20min
  gauges:       20 * 60 * 1000,   // 5min → 20min
  gas:          24 * 60 * 1000,   // 6min → 24min
  entergy:      20 * 60 * 1000,   // 5min → 20min
  hotspots:     40 * 60 * 1000,   // 10min → 40min
};

export function useLowPowerMode() {
  const [manual,   setManual]  = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [battery,  setBattery] = useState(null);   // { level, charging } | null

  // Read Battery Status API (Chrome/Android — not available on iOS)
  useEffect(() => {
    let bat = null;
    const init = async () => {
      try {
        bat = await navigator.getBattery?.();
        if (!bat) return;
        const update = () => setBattery({ level: bat.level, charging: bat.charging });
        update();
        bat.addEventListener('levelchange',   update);
        bat.addEventListener('chargingchange', update);
      } catch {}
    };
    init();
    return () => {
      if (bat) {
        bat.removeEventListener('levelchange',   () => {});
        bat.removeEventListener('chargingchange', () => {});
      }
    };
  }, []);

  // Active when manual OR battery is low and not charging
  const active = manual || (
    battery !== null &&
    battery.level <= LOW_BATTERY_THRESH &&
    !battery.charging
  );

  // Apply/remove CSS class on root for animation pausing
  useEffect(() => {
    if (active) {
      document.documentElement.classList.add('eos-low-power');
    } else {
      document.documentElement.classList.remove('eos-low-power');
    }
  }, [active]);

  const enable = useCallback(() => {
    setManual(true);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
  }, []);

  const disable = useCallback(() => {
    setManual(false);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }, []);

  const toggle = useCallback(() => {
    active ? disable() : enable();
  }, [active, enable, disable]);

  return {
    active,
    manual,
    battery,
    lowBattery: battery !== null && battery.level <= LOW_BATTERY_THRESH,
    batteryPct: battery !== null ? Math.round(battery.level * 100) : null,
    enable,
    disable,
    toggle,
    multiplier: LOW_POWER_MULTIPLIER,
    refreshIntervals: active ? LOW_POWER_REFRESH : null,
  };
}
